// Built-in choice icon shapes for the steps editor's icon picker. Mirrored
// from frontend/widget/src/icons.ts, which is the source of truth for both
// the shapes and the name list — the widget bundles these as inline SVG so
// they recolor via CSS variables (spec §7), and this module renders the same
// swatches inline in the admin's option rows. The names must stay identical
// to that file's (and surveys-core's BUILTIN_ICONS): the server rejects any
// other name at save time.
const wrap = (inner: string): string =>
    `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">${inner}</svg>`;

const smiley = (
    mouth: string,
    eyes = '<circle cx="8.5" cy="10" r="1.4"/><circle cx="15.5" cy="10" r="1.4"/>',
): string => wrap(`<circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/>${eyes}${mouth}`);

const ICONS: Record<string, string> = {
    star: wrap('<path d="M12 2l2.9 6.26 6.6.7-4.9 4.5 1.35 6.54L12 16.77 6.05 20l1.35-6.54-4.9-4.5 6.6-.7z"/>'),
    heart: wrap('<path d="M12 21c-4.8-3.6-8-6.6-8-10a4.6 4.6 0 0 1 8-3.2A4.6 4.6 0 0 1 20 11c0 3.4-3.2 6.4-8 10z"/>'),
    "thumb-up": wrap(
        '<path d="M3 10h3v11H3zM8 21h8.4a2 2 0 0 0 2-1.6l1.5-7A2 2 0 0 0 18 10h-5V5a2 2 0 0 0-2-2l-3 8z"/>',
    ),
    "thumb-down": wrap(
        '<path d="M21 14h-3V3h3zM16 3H7.6a2 2 0 0 0-2 1.6l-1.5 7A2 2 0 0 0 6 14h5v5a2 2 0 0 0 2 2l3-8z"/>',
    ),
    "smiley-angry": smiley(
        '<path d="M8 17c1-1.4 2.4-2 4-2s3 .6 4 2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
        '<path d="M6.5 8.5l3 1.2M17.5 8.5l-3 1.2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle cx="8.5" cy="11" r="1.4"/><circle cx="15.5" cy="11" r="1.4"/>',
    ),
    "smiley-sad": smiley(
        '<path d="M8 16.5c1-1.2 2.4-1.8 4-1.8s3 .6 4 1.8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
    ),
    "smiley-neutral": smiley(
        '<path d="M8 15.5h8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
    ),
    "smiley-happy": smiley(
        '<path d="M8 14.5c1 1.4 2.4 2 4 2s3-.6 4-2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
    ),
    "smiley-delighted": smiley('<path d="M7.5 14a4.8 4.8 0 0 0 9 0z"/>'),
    circle: wrap('<circle cx="12" cy="12" r="9"/>'),
};

/** The server's closed set (surveys-core's BUILTIN_ICONS), in the same
 *  order the widget declares them — used by the icon picker's swatch grid
 *  and by survey-schema.ts's validation. */
export const BUILTIN_ICON_NAMES = [
    "star",
    "heart",
    "thumb-up",
    "thumb-down",
    "smiley-angry",
    "smiley-sad",
    "smiley-neutral",
    "smiley-happy",
    "smiley-delighted",
    "circle",
] as const;

export function builtinIconSvg(name: string): string | undefined {
    return ICONS[name];
}
